import { PercentValue, ProgressMetadata } from "@factorearth/component-library";
import { getCurrentUser } from "aws-amplify/auth";
import { uploadData } from "aws-amplify/storage";
import { db } from "db/db";
import { exportDB, importInto, peakImportFile, ExportProgress, ImportOptions } from "dexie-export-import";
import JSZip from "jszip";
import { logSentry, recordSentry } from "utils/recordSentry";
import { buildDbExportManifest, computeRestoreSkipTables, windowedPercent } from "./dbExportHelpers";

type ImportProgress = Parameters<NonNullable<ImportOptions["progressCallback"]>>[0];

/**
 * Tables left out of the database export.
 * - imageBinary holds the raw photo Files. dexie-export-import reads every File into memory and JSON-encodes it, which
 *   killed the page on an iPad with ~900 photos before a single byte was uploaded. Photos have their own upload path
 *   (uploadImageBinariesToS3InBatches) that streams each File straight to S3.
 * - analyticsEvent is a self-flushing queue, not project data.
 * - sentryOffline is unused.
 */
export const DB_EXPORT_SKIP_TABLES = ["imageBinary", "analyticsEvent", "sentryOffline"];
/** Rows per JSON chunk. Each chunk becomes its own Blob slice, so memory stays bounded and progress ticks regularly. */
export const DB_EXPORT_ROWS_PER_CHUNK = 1000;

/**
 * Exports the offline database (minus DB_EXPORT_SKIP_TABLES) as a compact JSON blob.
 * @param opts.onProgress Called by dexie-export-import as rows are serialised
 * @param opts.skipTables Override of the default skip list
 */
export async function exportDatabaseAsBlob(opts?: { onProgress?: (progress: ExportProgress) => void; skipTables?: string[] }): Promise<Blob> {
	return exportDB(db, {
		skipTables: opts?.skipTables ?? DB_EXPORT_SKIP_TABLES,
		prettyJson: false,
		numRowsPerChunk: DB_EXPORT_ROWS_PER_CHUNK,
		progressCallback: (progress) => {
			opts?.onProgress?.(progress);
			return true;
		},
	});
}

/**
 * Row count per exported table, for the manifest written next to the JSON.
 */
export async function getExportRowCounts(skipTables: string[] = DB_EXPORT_SKIP_TABLES): Promise<Record<string, number>> {
	const tables = db.tables.filter((table) => !skipTables.includes(table.name));
	const counts = await Promise.all(tables.map((table) => table.count()));
	const result: Record<string, number> = {};
	tables.forEach((table, index) => {
		result[table.name] = counts[index];
	});
	return result;
}

export const downloadOfflineDatabaseAsJSON = async () => {
	const blob = await exportDatabaseAsBlob();
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	const user = await getCurrentUser();

	a.href = url;
	a.download = `${user.username}-indexeddb-export.json`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

type DbUploadPhase = "count" | "manifest" | "export" | "upload";

/**
 * Exports the offline database and uploads it to S3 under user-db-exports/<username>/.
 *
 * A small manifest (row counts, versions) is uploaded BEFORE the export starts. If the manifest exists in S3 but the
 * JSON does not, the export or upload failed on the device, which can be seen from the S3 console without Sentry.
 * @param setProgress Progress toast setter from useProgress
 * @param opts.appVersion App version string, recorded in the manifest and S3 metadata
 * @returns The S3 keys written, or undefined when the upload failed (the failure is reported to Sentry and the toast)
 */
export async function uploadOfflineDatabaseToS3(
	setProgress: (newProgress: Partial<ProgressMetadata>) => void,
	opts?: { appVersion?: string },
): Promise<{ jsonKey: string; manifestKey: string } | undefined> {
	const title = "Uploading Database";
	const toast = (progress: Partial<ProgressMetadata>) => setProgress({ title, variant: "info", canClose: false, show: true, ...progress });
	let phase: DbUploadPhase = "count";
	let jsonKey = "";
	let blobSize = 0;
	let rowCounts: Record<string, number> = {};
	try {
		toast({ value: 0, message: "Counting records..." });
		logSentry("DB export: counting rows");
		const user = await getCurrentUser();
		const uploadedAt = new Date().toISOString();
		const timestamp = uploadedAt.replace(/[:.]/g, "-");
		jsonKey = `user-db-exports/${user.username}/${timestamp}-indexeddb-export.json`;
		const manifestKey = `user-db-exports/${user.username}/${timestamp}-manifest.txt`;
		rowCounts = await getExportRowCounts();
		const totalRows = Object.values(rowCounts).reduce((sum, count) => sum + count, 0);

		phase = "manifest";
		toast({ value: 5, message: `Writing manifest (${totalRows} rows)...` });
		logSentry("DB export: uploading manifest", { manifestKey, rowCounts });
		const manifest = buildDbExportManifest({
			username: user.username,
			uploadedAt,
			appVersion: opts?.appVersion,
			dbVersion: db.verno,
			skippedTables: DB_EXPORT_SKIP_TABLES,
			rowCounts,
			jsonKey,
		});
		await uploadData({
			key: manifestKey,
			data: manifest,
			options: { contentType: "text/plain", metadata: { uploadedBy: user.username, uploadedAt } },
		}).result;

		phase = "export";
		toast({ value: 10, message: `Exporting ${totalRows} rows... keep the app open` });
		logSentry("DB export: exporting database");
		const blob = await exportDatabaseAsBlob({
			onProgress: (progress) => {
				const value = windowedPercent(progress.completedRows, progress.totalRows ?? totalRows, 10, 55) as PercentValue;
				toast({
					value,
					message: `Exporting tables ${progress.completedTables}/${progress.totalTables}, rows ${progress.completedRows}/${
						progress.totalRows ?? totalRows
					}... keep the app open`,
				});
			},
		});
		blobSize = blob.size;
		const sizeMb = (blobSize / (1024 * 1024)).toFixed(1);

		phase = "upload";
		toast({ value: 55, message: `Uploading ${sizeMb} MB to S3... keep the app open` });
		logSentry("DB export: uploading JSON", { jsonKey, blobSize });
		await uploadData({
			key: jsonKey,
			data: blob,
			options: {
				contentType: "application/json",
				metadata: { uploadedBy: user.username, uploadedAt, dbVersion: String(db.verno), appVersion: opts?.appVersion ?? "unknown" },
				onProgress: ({ transferredBytes, totalBytes }) => {
					const value = windowedPercent(transferredBytes, totalBytes ?? blobSize, 55, 99) as PercentValue;
					toast({ value, message: `Uploading ${sizeMb} MB to S3 (${value}%)... keep the app open` });
				},
			},
		}).result;

		toast({ value: 100, variant: "success", canClose: true, message: `Uploaded ${sizeMb} MB (${totalRows} rows) to ${jsonKey}` });
		logSentry("DB export: complete", { jsonKey, blobSize, totalRows });
		return { jsonKey, manifestKey };
	} catch (error: any) {
		recordSentry({ error, msg: "Error uploading database to S3", additionalData: { phase, jsonKey, blobSize, rowCounts } });
		const detail = `${error?.name ?? ""} ${error?.message ?? String(error)}`.trim();
		toast({ value: 100, variant: "destructive", canClose: true, message: `${phase} failed: ${detail}` });
		return undefined;
	}
}

/**
 * Reads the header of an export file (database name, version, tables and row counts) without importing it.
 */
export const peakDatabaseExport = (blob: Blob) => peakImportFile(blob);

/**
 * Restores an export produced by exportDatabaseAsBlob into the live database, then reloads the page.
 *
 * Imports INTO the app's own Dexie instance rather than recreating the database from the file. The export skips
 * tables (see DB_EXPORT_SKIP_TABLES), so recreating from the file would produce a database at the current version
 * that lacks those stores, and Dexie would then open it without upgrading and throw NotFoundError on first use.
 * Tables missing from the file are left untouched, so hydrated image binaries survive a repeat restore.
 * A database version mismatch is deliberately an error: the operator must run the app version that wrote the export
 * so the upgrade functions in db.ts are not bypassed.
 * @param blob The export file
 * @param onProgress Optional import progress callback
 */
export const restoreOfflineDatabaseFromJSON = async (blob: Blob, onProgress?: (progress: ImportProgress) => void) => {
	try {
		const meta = await peakImportFile(blob);
		const jsonTables = meta.data.tables.map((table) => table.name);
		const dbTables = db.tables.map((table) => table.name);
		const skipTables = computeRestoreSkipTables(dbTables, jsonTables);
		logSentry("DB restore: starting", { databaseVersion: meta.data.databaseVersion, appDbVersion: db.verno, skipTables });
		await db.open();
		await importInto(db, blob, {
			acceptMissingTables: true,
			clearTablesBeforeImport: true,
			overwriteValues: true,
			skipTables,
			progressCallback: (progress) => {
				onProgress?.(progress);
				return true;
			},
		});
		window.location.reload();
	} catch (error) {
		recordSentry({ error, msg: "Error restoring database from JSON export" });
		throw error;
	}
};

export async function downloadImageBinaries(onProgress: (progress: PercentValue, message: string) => void) {
	try {
		onProgress(0, "Loading images...");
		logSentry("Beginning the image download process");

		const images = await db.imageBinary.toArray();
		onProgress(20, `Processing ${images.length} images...`);
		logSentry(`Found ${images.length} images to download`);

		// If no images, notify and exit early
		if (!images || images.length === 0) {
			onProgress?.(100, "No images to download.");
			return;
		}

		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

		if (isIOS && images.length > 50) {
			return await downloadImageBinariesInBatches(images, 20, onProgress);
		}

		const zip = new JSZip();

		images.forEach((image, index) => {
			zip.file(`${image.key}.${image.binary.type}`, image.binary);
			if (index % 10 === 0) {
				const currentProgess = (20 + (index / images.length) * 60) as PercentValue;
				onProgress?.(currentProgess, `Adding image ${index + 1}/${images.length}...`);
				logSentry(`Adding image ${index + 1}/${images.length}...`);
			}
		});

		onProgress?.(80, "Creating ZIP file...");
		logSentry("Creating ZIP file with image binaries");
		const blob = await zip.generateAsync({
			type: "blob",
			compression: "DEFLATE",
			compressionOptions: { level: 1 },
		});

		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");

		onProgress?.(95, "Starting download...");
		logSentry("Starting download of ZIP file");
		const user = await getCurrentUser();
		if (isIOS) {
			await downloadBlobIOS(blob, `${user.username}-images.zip`);
		} else {
			a.href = url;
			a.download = `${user.username}-images.zip`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		}

		onProgress?.(100, "Download complete!");
		logSentry("Download complete!");
	} catch (error) {
		recordSentry({ error, msg: "Error downloading image binaries" });
	}
}

async function downloadBlobIOS(blob: Blob, filename: string) {
	// Create a temporary URL that works better on iOS
	const url = URL.createObjectURL(blob);

	// Open in new window for iOS
	const newWindow = window.open(url, "_blank");

	if (!newWindow) {
		// Fallback: try direct assignment
		window.location.href = url;
	}

	// Clean up after a delay
	setTimeout(() => {
		URL.revokeObjectURL(url);
		if (newWindow) {
			newWindow.close();
		}
	}, 1000);
}

async function downloadImageBinariesInBatches(images: any[], batchSize = 20, onProgress: (progress: PercentValue, message: string) => void) {
	const user = await getCurrentUser();
	const totalBatches = Math.ceil(images.length / batchSize);
	onProgress(20, `Preparing to download ${images.length} images in ${totalBatches} batches...`);
	logSentry(`Preparing to download ${images.length} images in ${totalBatches} batches...`);
	for (let i = 0; i < totalBatches; i++) {
		const startIndex = i * batchSize;
		const endIndex = Math.min(startIndex + batchSize, images.length);
		const batch = images.slice(startIndex, endIndex);

		const zip = new JSZip();
		batch.forEach((image, index) => {
			zip.file(`${image.key}.${image.binary.type}`, image.binary);
			// Calculate progress: 20% start + progress within 60% range
			// Current batch progress + progress from completed batches
			const batchProgress = (index + 1) / batch.length; // Progress within current batch (0-1)
			const completedBatchesProgress = i / totalBatches; // Progress from completed batches (0-1)
			const totalProgress = completedBatchesProgress + batchProgress / totalBatches;
			const currentProgress = (20 + totalProgress * 60) as PercentValue;

			if (index % 5 === 0 || index === batch.length - 1) {
				// Update more frequently for smaller batches
				onProgress(currentProgress, `Batch ${i + 1}/${totalBatches}: Adding image ${index + 1}/${batch.length}...`);
				logSentry(`Batch ${i + 1}/${totalBatches}: Adding image ${index + 1}/${batch.length}...`);
			}
		});

		// Update progress after each batch is zipped
		const batchCompleteProgress = (20 + ((i + 1) / totalBatches) * 60) as PercentValue;
		onProgress(batchCompleteProgress, `Batch ${i + 1}/${totalBatches}: Creating ZIP file...`);

		const blob = await zip.generateAsync({
			type: "blob",
			compression: "DEFLATE",
			compressionOptions: { level: 1 },
		});

		const filename = `${user.username}-images-batch-${i + 1}-of-${totalBatches}.zip`;

		// Small delay between downloads to prevent iOS issues
		if (i > 0) {
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
		logSentry(`Batch ${i + 1}/${totalBatches}: Downloading ZIP file ${filename}`);
		await downloadBlobIOS(blob, filename);
	}
	onProgress(100, "All batches downloaded successfully!");
	logSentry("All batches downloaded successfully!");
}

// Batch upload with retry logic for large datasets
export async function uploadImageBinariesToS3InBatches(setProgress: (newProgress: Partial<ProgressMetadata>) => void, batchSize = 10) {
	try {
		// const storageConfig = Amplify.getConfig().Storage;
		// console.log('Current S3 Storage Config:', storageConfig);
		setProgress({ value: 0, variant: "info", message: "Loading images...", title: "Uploading Images", canClose: false, show: true });
		const images = await db.imageBinary.toArray();
		setProgress({ value: 10, variant: "info", message: `Processing ${images.length} images...`, title: "Uploading Images", canClose: false, show: true });
		if (!images || images.length === 0) {
			setProgress?.({ value: 100, variant: "info", message: "No images to upload.", title: "Uploading Images", canClose: true, show: true });
			return [];
		}

		const user = await getCurrentUser();
		const totalBatches = Math.ceil(images.length / batchSize);
		const allResults: { key: string; success: boolean; error?: string }[] = [];

		setProgress({ value: 20, variant: "info", message: `Uploading in ${totalBatches} batches...`, title: "Uploading Images", canClose: false, show: true });
		const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
		for (let i = 0; i < totalBatches; i++) {
			const startIndex = i * batchSize;
			const endIndex = Math.min(startIndex + batchSize, images.length);
			const batch = images.slice(startIndex, endIndex);

			const batchResults = await Promise.all(
				batch.map(async (image, batchIndex) => {
					try {
						const fileExtension = getFileExtension(image.binary.type);
						const s3Key = `user-image-binary-uploads/${user.username}/images/${timestamp}/${image.key}.${fileExtension}`;
						const uploadResult = await uploadData({
							key: s3Key,
							data: image.binary,
							options: {
								contentType: image.binary.type,
								metadata: {
									originalKey: image.key,
									batchNumber: (i + 1).toString(),
									uploadedBy: user.username,
									uploadedAt: new Date().toISOString(),
								},
							},
						});

						await uploadResult.result;
						return { key: s3Key, success: true };
					} catch (error: any) {
						const errorMsg = `Failed to upload image ${image.key}: ${error.message}`;
						recordSentry({ error, msg: errorMsg });
						return { key: image.key, success: false, error: error.message };
					}
				}),
			);

			allResults.push(...batchResults);

			const progress = (20 + ((i + 1) / totalBatches) * 70) as PercentValue;
			const successInBatch = batchResults.filter((r) => r.success).length;
			setProgress({
				value: progress,
				variant: "info",
				message: `Batch ${i + 1}/${totalBatches}: ${successInBatch}/${batch.length} uploaded`,
				title: "Uploading Images",
				canClose: false,
				show: true,
			});

			// Delay between batches to avoid rate limiting
			if (i < totalBatches - 1) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
		}
		// const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
		const totalSuccess = allResults.filter((r) => r.success).length;
		const totalFailed = allResults.filter((r) => !r.success).length;
		let resultContent = allResults.map((r) => `${r.key}: ${r.success ? "Success" : `Failed - ${r.error}`}`).join("\n");
		resultContent += `\n\nTotal Success: ${totalSuccess}\nTotal Failed: ${totalFailed}`;
		const resultDataPath = `user-image-binary-uploads/${user.username}/results/${timestamp}.txt`;
		console.log(`putting results here: `, resultDataPath);
		const uploadResult = await uploadData({
			key: resultDataPath,
			data: resultContent,
			options: {
				contentType: "text/plain",
				metadata: {
					uploadedBy: user.username,
					uploadedAt: new Date().toISOString(),
				},
			},
		});

		await uploadResult.result;
		setProgress({
			value: 100,
			variant: "info",
			message: `Upload complete! ${totalSuccess} successful, ${totalFailed} failed.`,
			title: "Uploading Images",
			canClose: true,
			show: true,
		});
		return allResults;
	} catch (error: any) {
		recordSentry({ error, msg: "Error in batched upload to S3" });
		const detail = `${error?.name ?? ""} ${error?.message ?? String(error)}`.trim();
		setProgress({
			value: 100,
			variant: "destructive",
			message: `Upload failed: ${detail}`,
			title: "Uploading Images",
			canClose: true,
			show: true,
		});
		// Reported above and shown in the toast; rethrowing only produced an unhandled rejection in the Profile page.
		return [];
	}
}

// Helper function to get file extension from MIME type
function getFileExtension(mimeType: string): string {
	const extensions: { [key: string]: string } = {
		"image/jpeg": "jpg",
		"image/jpg": "jpg",
		"image/png": "png",
		"image/gif": "gif",
		"image/webp": "webp",
		"image/bmp": "bmp",
		"image/tiff": "tiff",
		"image/svg+xml": "svg",
	};
	return extensions[mimeType] || "jpg";
}
